/**
 * EL HISTORIAL DEL COPILOTO — la parte pura de la pantalla.
 *
 * Vive acá y no en el componente porque son decisiones que se pueden probar: qué
 * palabras ve el dueño para cada origen, y cómo sus filtros se vuelven la
 * consulta. Las fechas son lo delicado: el dueño elige DÍAS en su hora local, y
 * la API filtra por instantes.
 */

/** Los orígenes, en el orden y con las palabras que se ven en el panel. */
export const ORIGENES_HISTORIAL = [
  { valor: 'panel-flotante', etiqueta: 'Botón flotante' },
  { valor: 'panel-asistente', etiqueta: 'Pantalla del asistente' },
  { valor: 'whatsapp', etiqueta: 'WhatsApp' },
  { valor: 'puesta-en-marcha', etiqueta: 'Puesta en marcha' },
  { valor: 'entrevista-pedido', etiqueta: 'Pedido de cambio' },
] as const;

export type OrigenHistorial = (typeof ORIGENES_HISTORIAL)[number]['valor'];

/**
 * El nombre de un origen. PURO.
 *
 * Sin origen (una fila de antes del historial, escrita desde el panel) dice
 * "Panel": no se sabe si fue el botón o la pantalla, y adivinar sería peor.
 */
export function etiquetaDeOrigen(origen: string | null | undefined): string {
  return ORIGENES_HISTORIAL.find((o) => o.valor === origen)?.etiqueta ?? 'Panel';
}

/**
 * La marca de un mensaje que no fue una respuesta pensada. `null` si lo fue.
 * PURO.
 */
export function etiquetaDeTipo(tipo: string | null | undefined): string | null {
  if (tipo === 'aviso-sistema') return 'Aviso del sistema';
  if (tipo === 'error') return 'No pudo contestar';
  return null;
}

/** Lo que el dueño elige en la pantalla. Las fechas, como las da un `<input type="date">`. */
export interface FiltrosHistorial {
  persona: string;
  origen: string;
  /** `YYYY-MM-DD`, incluido. */
  desde: string;
  /** `YYYY-MM-DD`, incluido: se manda la medianoche del día siguiente. */
  hasta: string;
  q: string;
}

export const FILTROS_VACIOS: FiltrosHistorial = {
  persona: '',
  origen: '',
  desde: '',
  hasta: '',
  q: '',
};

/** La medianoche LOCAL de un `YYYY-MM-DD`, corrida `dias`. `null` si no es una fecha. */
function medianocheLocal(dia: string, dias = 0): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia);
  if (!m) return null;
  // Con el constructor de partes, no con un string: `new Date('2026-09-01')`
  // se lee como medianoche UTC, que en República Dominicana es la tarde del día
  // anterior.
  const fecha = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + dias);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
}

/**
 * Los filtros como consulta para la API. PURO.
 *
 * Solo viaja lo que tiene valor. Un origen que no existe no se manda: la API lo
 * rechazaría, y un 422 por un filtro raro deja la pantalla en blanco.
 */
export function consultaDeHistorial(
  f: FiltrosHistorial,
  cursor?: string | null,
): Record<string, string> {
  const c: Record<string, string> = {};
  if (f.persona) c.persona = f.persona;
  if (ORIGENES_HISTORIAL.some((o) => o.valor === f.origen)) c.origen = f.origen;
  const desde = medianocheLocal(f.desde);
  if (desde) c.desde = desde.toISOString();
  // La API toma `hasta` como EXCLUIDO: la medianoche del día siguiente hace que
  // el día elegido entre entero.
  const hasta = medianocheLocal(f.hasta, 1);
  if (hasta) c.hasta = hasta.toISOString();
  const q = f.q.trim();
  if (q) c.q = q;
  if (cursor) c.cursor = cursor;
  return c;
}

/** ¿Hay algún filtro puesto? Cambia qué se dice cuando la lista queda vacía. PURO. */
export function hayFiltros(f: FiltrosHistorial): boolean {
  return Boolean(f.persona || f.origen || f.desde || f.hasta || f.q.trim());
}

/**
 * Junta las páginas de "Cargar más" sin repetir. PURO.
 *
 * La lista pagina por posición: si entra un mensaje mientras se mira, un hilo
 * puede venir en dos páginas, y mostrarlo dos veces parece un error.
 */
export function juntarPorId<T extends { id: string }>(paginas: T[][]): T[] {
  const vistos = new Set<string>();
  const todos: T[] = [];
  for (const pagina of paginas) {
    for (const x of pagina) {
      if (vistos.has(x.id)) continue;
      vistos.add(x.id);
      todos.push(x);
    }
  }
  return todos;
}
